import {
  BarChart3,
  Bell,
  CreditCard,
  FileBarChart,
  Gauge,
  Map as MapIcon,
  MoreHorizontal,
  PlugZap,
  Settings,
  UserCircle,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { MessageKey } from '@qronstat/i18n';
import { actionPermissions, sectionPermissions } from '@qronstat/shared';

export type TabKey = 'summary' | 'rentals' | 'stations' | 'clients' | 'more';

export interface NavLeaf {
  key: string;
  titleKey: MessageKey;
  path: string;
  permissions?: string[];
}

export const documentsPermissions = [sectionPermissions.payments, actionPermissions.acquiringDocuments];

export function visibleLeaves(leaves: NavLeaf[], can: (code: string) => boolean): NavLeaf[] {
  return leaves.filter((leaf) => !leaf.permissions || leaf.permissions.every((p) => can(p)));
}

export interface NavSection {
  key: string;
  titleKey: MessageKey;
  path: string;
  tab: TabKey;
  icon: LucideIcon;
  children: NavLeaf[];
  permission: string | null;
}

function leaves(section: string, keys: string[]): NavLeaf[] {
  return keys.map((key) => ({
    key,
    titleKey: `nav.${section}.${key}` as MessageKey,
    path: `/${section}/${key}`,
  }));
}

export const navSections: NavSection[] = [
  {
    key: 'summary',
    titleKey: 'nav.summary',
    path: '/',
    tab: 'summary',
    icon: Gauge,
    children: [],
    permission: sectionPermissions.summary,
  },
  {
    key: 'rentals',
    titleKey: 'nav.rentals',
    path: '/rentals/overview',
    tab: 'rentals',
    icon: BarChart3,
    children: leaves('rentals', ['overview', 'revenue', 'debts', 'refusals', 'promo', 'duration', 'tariffs']),
    permission: sectionPermissions.rentals,
  },
  {
    key: 'stations',
    titleKey: 'nav.stations',
    path: '/stations/overview',
    tab: 'stations',
    icon: PlugZap,
    children: leaves('stations', [
      'overview',
      'online',
      'health',
      'new',
      'idle',
      'powerbanks',
      'batteries',
      'locations',
      'groups',
    ]),
    permission: sectionPermissions.stations,
  },
  {
    key: 'coverage',
    titleKey: 'nav.coverage',
    path: '/coverage',
    tab: 'more',
    icon: MapIcon,
    children: [],
    permission: sectionPermissions.stations,
  },
  {
    key: 'clients',
    titleKey: 'nav.clients',
    path: '/clients/registrations',
    tab: 'clients',
    icon: Users,
    children: leaves('clients', ['registrations', 'retention', 'devices', 'cards', 'funnel']),
    permission: sectionPermissions.clients,
  },
  {
    key: 'payments',
    titleKey: 'nav.payments',
    path: '/payments/overview',
    tab: 'more',
    icon: CreditCard,
    children: [
      ...leaves('payments', ['overview', 'counterparties', 'methods', 'receipts', 'reconciliation']),
      {
        key: 'documents',
        titleKey: 'nav.payments.documents',
        path: '/payments/documents',
        permissions: documentsPermissions,
      },
    ],
    permission: sectionPermissions.payments,
  },
  {
    key: 'reports',
    titleKey: 'nav.reports',
    path: '/reports',
    tab: 'more',
    icon: FileBarChart,
    children: [
      { key: 'list', titleKey: 'nav.reports.list', path: '/reports' },
      { key: 'stations', titleKey: 'nav.reports.stations', path: '/reports/stations' },
      {
        key: 'documents',
        titleKey: 'nav.reports.documents',
        path: '/payments/documents',
        permissions: documentsPermissions,
      },
      { key: 'templates', titleKey: 'nav.reports.templates', path: '/reports/templates' },
      { key: 'marks', titleKey: 'nav.reports.marks', path: '/reports/marks' },
    ],
    permission: sectionPermissions.reports,
  },
  {
    key: 'alerts',
    titleKey: 'nav.alerts',
    path: '/alerts',
    tab: 'more',
    icon: Bell,
    children: [],
    permission: sectionPermissions.alerts,
  },
  {
    key: 'settings',
    titleKey: 'nav.settings',
    path: '/settings/users',
    tab: 'more',
    icon: Settings,
    children: [],
    permission: sectionPermissions.settings,
  },
  {
    key: 'profile',
    titleKey: 'nav.profile',
    path: '/profile',
    tab: 'more',
    icon: UserCircle,
    children: [],
    permission: null,
  },
];

export interface TabDef {
  key: TabKey;
  titleKey: MessageKey;
  path: string;
  icon: LucideIcon;
}

export const tabs: TabDef[] = [
  { key: 'summary', titleKey: 'nav.summary', path: '/', icon: Gauge },
  { key: 'rentals', titleKey: 'nav.rentals', path: '/rentals/overview', icon: BarChart3 },
  { key: 'stations', titleKey: 'nav.stations', path: '/stations/overview', icon: PlugZap },
  { key: 'clients', titleKey: 'nav.clients', path: '/clients/registrations', icon: Users },
  { key: 'more', titleKey: 'nav.more', path: '/more', icon: MoreHorizontal },
];

export function sectionForPath(pathname: string): NavSection | undefined {
  if (pathname === '/') return navSections[0];
  return navSections.find((s) => s.key !== 'summary' && pathname.startsWith(`/${s.key}`));
}

export function tabForPath(pathname: string): TabKey {
  if (pathname === '/more') return 'more';
  if (pathname.startsWith('/objects/station') || pathname.startsWith('/objects/location')) return 'stations';
  return sectionForPath(pathname)?.tab ?? 'more';
}

export const moreSheetOrder = [
  'alerts',
  'coverage',
  'payments',
  'reports',
  'search',
  'settings',
  'profile',
] as const;
export type MoreSheetKey = (typeof moreSheetOrder)[number];
